import { css } from '@linaria/atomic';
import { theme } from '@frontend/theme';

export const GRID_AREAS = {
  CONTENT: 'content',
  META: 'meta',
  AVATAR: 'avatar',
  EMPTY: 'empty',
} as const;

const containerInboundStyle: string = css`
  width: 100%;
  display: grid;
  grid-template-rows: 1fr auto;
  grid-template-columns: auto 1fr;
  padding: 0;
  gap: ${theme.spacing(0.5, 1)};
  grid-template-areas:
    'avatar content'
    'empty meta';
  align-items: end;
  margin-top: ${theme.spacing(1)};
`;

const containerOutboundStyle: string = css`
  width: 100%;
  display: grid;
  grid-template-rows: 1fr auto;
  grid-template-columns: 1fr auto;
  padding: 0;
  gap: ${theme.spacing(0.5, 1)};
  grid-template-areas:
    'content avatar'
    'meta empty';
  align-items: end;
  margin-top: ${theme.spacing(1)};
`;

const containerStyles = ({ direction }: { direction: 'inbound' | 'outbound' }) => {
  return direction === 'outbound' ? containerOutboundStyle : containerInboundStyle;
};

const contentInboundStyle: string = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: ${theme.spacing(1)};
  position: relative;
  grid-area: content;

  .bubbles-container {
    display: flex;
    flex-direction: column;
    max-width: min(95%, 600px);
    gap: ${theme.spacing(1)};
    align-items: start;
  }
`;

const contentOutboundStyle: string = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: ${theme.spacing(1)};
  position: relative;
  grid-area: content;

  .bubbles-container {
    display: flex;
    flex-direction: column;
    max-width: min(95%, 600px);
    gap: ${theme.spacing(1)};
    align-items: end;
  }
`;

const contentStyle = ({ direction }: { direction: 'inbound' | 'outbound' }) => {
  return direction === 'outbound' ? contentOutboundStyle : contentInboundStyle;
};

const metaInfoInboundStyle: string = css`
  grid-area: meta;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .tags-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding-bottom: ${theme.spacing(0.5)};
    justify-content: flex-start;
    align-items: flex-start;
    gap: ${theme.spacing(1)};
  }

  .meta-info-container {
    display: flex;
    gap: ${theme.spacing(1)};
    justify-content: flex-start;
    flex-direction: row-reverse;
    padding-bottom: ${theme.spacing(0.5)};
  }

  .metadata-container {
    display: flex;
    gap: ${theme.spacing(1)};
  }
`;

const metaInfoOutboundStyle: string = css`
  grid-area: meta;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  .tags-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding-bottom: ${theme.spacing(0.5)};
    justify-content: flex-end;
    align-items: flex-end;
    gap: ${theme.spacing(1)};
  }

  .meta-info-container {
    display: flex;
    gap: ${theme.spacing(1)};
    justify-content: flex-end;
    flex-direction: row;
    padding-bottom: ${theme.spacing(0.5)};
  }

  .metadata-container {
    display: flex;
    gap: ${theme.spacing(1)};
  }
`;

const metaInfoStyle = ({ direction }: { direction: 'inbound' | 'outbound' }) => {
  return direction === 'outbound' ? metaInfoOutboundStyle : metaInfoInboundStyle;
};

export const bubbleStylesStatic: string = css`
  list-style-type: none;
  gap: ${theme.spacing(2)};
  border-radius: ${theme.borderRadius.medium};
  white-space: pre-line;
  width: fit-content;

  .img-bubble {
    width: 100%;
    min-height: 150px;
    background-color: ${theme.colors.neutral5};
    border-radius: ${theme.borderRadius.medium};
  }

  .video-bubble-wrapper {
    position: relative;
    display: block;
    width: 100%;
    max-height: inherit;
    border-radius: ${theme.borderRadius.medium};
    overflow: hidden;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary50};
      outline-offset: 2px;
    }
  }

  .video-bubble-video {
    display: block;
    width: 100%;
    height: auto;
    max-height: inherit;
  }

  .video-bubble-play {
    position: absolute;
    left: ${theme.spacing(1.5)};
    bottom: ${theme.spacing(1.5)};
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.55);
    color: ${theme.colors.white};
    border: none;
    padding: 0;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover,
    &:focus-visible {
      background-color: rgba(0, 0, 0, 0.75);
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary50};
      outline-offset: 2px;
    }
  }

  .pdf-bubble-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${theme.spacing(1)};
  }

  .pdf-bubble-preview {
    position: relative;
    width: 100%;
    /* US Letter aspect ratio so a single page fills the box without showing the next */
    aspect-ratio: 8.5 / 11;
    max-height: 320px;
    background-color: ${theme.colors.white};
    overflow: hidden;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary50};
      outline-offset: 2px;
    }
  }

  .pdf-bubble-preview-object {
    /* Oversize and offset to crop out the PDF viewer's gray gutter on all sides */
    position: absolute;
    top: -4%;
    left: -4%;
    width: 108%;
    height: 108%;
    border: none;
    pointer-events: none;
    background-color: ${theme.colors.white};
  }

  .pdf-bubble-preview-overlay {
    position: absolute;
    inset: 0;
    background: transparent;
  }

  .pdf-bubble {
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
    padding: ${theme.spacing(1)};
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    background: transparent;
    border: none;
    border-radius: ${theme.borderRadius.small};
    cursor: pointer;
    text-align: left;

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary50};
      outline-offset: 2px;
    }
  }

  .pdf-bubble-icon {
    position: relative;
    flex-shrink: 0;
    width: 32px;
    height: 40px;
    color: ${theme.colors.white};
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
  }

  .pdf-bubble-icon-shape {
    position: absolute;
    inset: 0;
    display: block;
  }

  .pdf-bubble-badge {
    position: absolute;
    top: 60%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.5px;
    color: ${theme.colors.primary50};
    pointer-events: none;
  }

  .pdf-bubble-info {
    flex: 1 0 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  }

  .pdf-bubble-filename {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
  }

  .pdf-bubble-filesize {
    margin: 0;
  }
`;

export const bubbleInboundStyle: string = css`
  .pdf-bubble {
    &:hover,
    &:focus-visible {
      background-color: ${theme.colors.neutral10};
    }
  }
`;

export const bubbleOutboundStyle: string = css`
  .pdf-bubble {
    &:hover,
    &:focus-visible {
      background-color: ${theme.colors.primary10};
    }
  }
`;

const bubbleStyles = (): string => {
  return bubbleStylesStatic;
};

export const easeInOutVariants = {
  visible: { opacity: 1, transition: { duration: 0.3 } },
  hidden: { opacity: 0, transition: { duration: 0.3 } },
};

export const slideInVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
};

export const ChatStyles = {
  container: containerStyles,
  content: contentStyle,
  meta: metaInfoStyle,
  actions: css`
    border-width: 1px;
    border-style: solid;
    border-color: ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.small};
    box-shadow: ${theme.shadows.light};
    position: absolute;
    top: ${theme.spacing(-2)};
    background-color: ${theme.colors.white};
    display: flex;
    z-index: ${theme.zIndex.popover};
    padding-top: ${theme.spacing(0.25)};
    padding-right: ${theme.spacing(0.25)};
    padding-bottom: ${theme.spacing(0.25)};
    padding-left: ${theme.spacing(0.25)};

    & button {
      padding-top: ${theme.spacing(0.5)};
      padding-right: ${theme.spacing(0.5)};
      padding-bottom: ${theme.spacing(0.5)};
      padding-left: ${theme.spacing(0.5)};
      height: unset;
    }
  `,
  bubble: bubbleStyles,
  bubbleInbound: bubbleInboundStyle,
  bubbleOutbound: bubbleOutboundStyle,
  previewModal: css`
    padding-top: 0;
    padding-right: 0;
    padding-bottom: 0;
    padding-left: 0;
    max-width: unset;
    overflow-x: visible;
    overflow-y: visible;

    .wv-modal-flex-content {
      padding-top: 0;
      padding-right: 0;
      padding-bottom: 0;
      padding-left: 0;
      max-height: 78vh;
    }

    img {
      max-height: inherit;
    }

    .btn-containers {
      position: absolute;
      display: flex;
      gap: ${theme.spacing(1)};
      top: ${theme.spacing(-4)};
      right: 0;
    }

    .download-button {
      background-color: ${theme.colors.white};
      height: ${theme.spacing(3)};
      width: ${theme.spacing(3)};
      padding-top: 0;
      padding-right: 0;
      padding-bottom: 0;
      padding-left: 0;
    }

    .close-button {
      background-color: ${theme.colors.white};
      height: ${theme.spacing(3)};
      width: ${theme.spacing(3)};
      padding-top: 0;
      padding-right: 0;
      padding-bottom: 0;
      padding-left: 0;
    }
  `,
};
